/**
 * DialogProvider — the in-page replacements for window.confirm and
 * window.alert, mounted once in App.jsx (and the Storybook decorator).
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ message: t('…'), confirmLabel: t('common.delete'), destructive: true }))) return;
 *
 *   const notice = useNotice();
 *   notice.error(t('…'));      // a failed action; stays 8 s, or until closed
 *   notice.success(t('…'));    // a finished one; 4 s
 *
 * Every confirm is the same ConfirmDialog and every notice the same Toast,
 * so the dialogs look alike wherever they come from. The hooks work inside
 * plain hooks too (useLeaveGuard, useConsequenceFlow), which is why they are
 * not components. See wiki/pages/no-browser-modal-dialogs.md.
 */
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import ConfirmDialog from './ConfirmDialog';
import ToastStack from './Toast';

const ConfirmContext = createContext(null);
const NoticeContext = createContext(null);

export const NOTICE_MS = { error: 8000, success: 4000, info: 4000 };

// A string is the message; an object carries ConfirmDialog's props.
export function toConfirmOptions(input) {
  return typeof input === 'string' ? { message: input } : { ...(input || {}) };
}

export function DialogProvider({ children }) {
  const [pending, setPending] = useState(null);
  const [notices, setNotices] = useState([]);
  const nextId = useRef(0);
  const timers = useRef(new Map());

  const confirm = useCallback((input) => new Promise((resolve) => {
    const options = toConfirmOptions(input);
    // A second confirm while one is open answers the first with "no": the
    // user never saw it, and two stacked dialogs would be the browser's
    // behaviour we are replacing.
    setPending((current) => {
      current?.resolve(false);
      return { options, resolve };
    });
  }), []);

  const answer = useCallback((value) => {
    setPending((current) => {
      current?.resolve(value);
      return null;
    });
  }, []);

  const close = useCallback((id) => {
    clearTimeout(timers.current.get(id));
    timers.current.delete(id);
    setNotices((list) => list.filter((n) => n.id !== id));
  }, []);

  const show = useCallback((tone, message) => {
    const id = ++nextId.current;
    setNotices((list) => [...list, { id, tone, message: String(message ?? '') }]);
    timers.current.set(id, setTimeout(() => close(id), NOTICE_MS[tone] || NOTICE_MS.info));
    return id;
  }, [close]);

  const notice = useMemo(() => ({
    error: (message) => show('error', message),
    success: (message) => show('success', message),
    info: (message) => show('info', message),
    close,
  }), [show, close]);

  return (
    <ConfirmContext.Provider value={confirm}>
      <NoticeContext.Provider value={notice}>
        {children}
        {pending && (
          <ConfirmDialog
            {...pending.options}
            onConfirm={() => answer(true)}
            onCancel={() => answer(false)}
          />
        )}
        <ToastStack notices={notices} onClose={close} />
      </NoticeContext.Provider>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const confirm = useContext(ConfirmContext);
  if (!confirm) throw new Error('useConfirm must be used within a DialogProvider');
  return confirm;
}

export function useNotice() {
  const notice = useContext(NoticeContext);
  if (!notice) throw new Error('useNotice must be used within a DialogProvider');
  return notice;
}
